import { Link } from "react-router-dom";
import { useV2Sectors } from "./useV2Data";
import { SectionHead } from "@/components/home/shared";

type Portal = {
  slug: string; name: string; desc: string; route: string;
  accent: string; textAccent?: string; art: string; isNew?: boolean;
};

const PORTALS: Portal[] = [
  { slug: "crypto", name: "Crypto", desc: "Digital assets, tokens and the broader crypto market ecosystem.", route: "/crypto", accent: "#c24dff", art: "/portals2/crypto.jpg" },
  { slug: "defi", name: "DeFi", desc: "Decentralized finance protocols, platforms and innovations.", route: "/defi", accent: "#05d1e5", art: "/portals2/defi.jpg" },
  { slug: "web3", name: "Web3", desc: "The decentralized web and next-generation internet infrastructure.", route: "/web3", accent: "#0f52ff", textAccent: "#4d7dff", art: "/portals2/web3.jpg" },
  { slug: "blockchain", name: "Blockchain", desc: "Layer-1s, scaling, interoperability and core infrastructure.", route: "/blockchain", accent: "#2f6bff", textAccent: "#5d87ff", art: "/portals2/blockchain.jpg" },
  { slug: "government-policy", name: "Policy", desc: "Regulation, policy developments and government oversight.", route: "/policy", accent: "#28d9a1", art: "/portals2/policy.jpg" },
  { slug: "research", name: "Research", desc: "In-depth analysis, reports and market intelligence.", route: "/research", accent: "#c24dff", art: "/portals2/research.jpg" },
  { slug: "ai", name: "Artificial Intelligence", desc: "AI agents, models, compute networks and onchain AI.", route: "/ai", accent: "#f04dff", art: "/portals2/ai.jpg" },
  { slug: "quantum", name: "Quantum", desc: "Quantum computing, PQC, infra and next-gen cryptographic security.", route: "/quantum", accent: "#05d1e5", art: "/portals2/quantum.jpg" },
];

const fmtStories = (n?: number) => {
  if (!n) return null;
  const v = n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K` : String(n);
  return `${v} ${n === 1 ? "story" : "stories"}`;
};

const PortalCard = ({ p, total }: { p: Portal; total?: number }) => {
  const stories = fmtStories(total);
  return (
    <Link to={p.route} data-testid={`v2-signal-portal-${p.slug}`}
      className="group relative flex flex-col shrink-0 snap-start w-[180px] sm:w-[200px] xl:w-auto rounded-[12px] overflow-hidden bg-[#04060d] transition-transform duration-200 hover:-translate-y-1 motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c24dff]/60"
      style={{ border: `1px solid ${p.accent}40`, boxShadow: `0 0 24px ${p.accent}12` }}>
      <span className="relative block aspect-[203/243] overflow-hidden">
        <img src={p.art} alt={`${p.name} sector artwork`} loading="lazy" decoding="async"
          className="absolute inset-0 w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-500 group-hover:scale-[1.045] motion-reduce:group-hover:scale-100" />
        <span aria-hidden className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#04060d] to-transparent" />
      </span>
      <span className="relative flex flex-col flex-1 px-4 pb-4 -mt-1.5">
        {p.isNew && (
          <span data-testid={`v2-signal-portal-${p.slug}-new`}
            className="self-start mb-2 inline-flex items-center rounded-full border px-2 py-[2px] nl-mono text-[9px] font-medium tracking-[0.08em]"
            style={{ borderColor: `${p.accent}99`, color: p.accent, backgroundColor: "#07040b" }}>NEW</span>
        )}
        <span className="nl-sg font-medium text-[19px] leading-[1.2] text-[#f5f7ff]">{p.name}</span>
        <span className="nl-geist text-[11px] leading-[1.4] text-[#a1abbd] mt-1.5 line-clamp-4">{p.desc}</span>
        <span className="mt-auto pt-3 flex items-center justify-between gap-2">
          {stories ? (
            <span className="nl-mono text-[10.5px] font-medium truncate" style={{ color: p.textAccent || p.accent }}
              data-testid={`v2-signal-portal-${p.slug}-stories`}>{stories}</span>
          ) : (
            <span className="nl-geist text-[10.5px] font-medium text-[#a1abbd]">Explore sector</span>
          )}
          <span aria-hidden className="nl-sg text-[15px] leading-none text-[#f5f7ff] transition-transform duration-200 group-hover:translate-x-[3px] motion-reduce:transition-none">→</span>
        </span>
      </span>
      <span aria-hidden className="pointer-events-none absolute inset-0 rounded-[12px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 motion-reduce:transition-none"
        style={{ boxShadow: `inset 0 0 0 1px ${p.accent}90, 0 8px 32px ${p.accent}22` }} />
    </Link>
  );
};

export const SignalPortals = () => {
  const { data: sectors } = useV2Sectors();
  const totals: Record<string, number> = sectors?.totals || {};
  return (
    <section data-testid="v2-signal-portals" className="mt-[56px]">
      <SectionHead title="Explore the Onchain Economy"
        sub="Discover the sectors driving blockchain innovation, growth, and global adoption."
        cta="View all sectors" to="/latest" testid="v2-signal-portals-cta" module="signal_portals" />
      <div data-testid="v2-signal-portals-rail"
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 xl:grid xl:grid-cols-8 xl:gap-3 xl:overflow-visible xl:pb-0">
        {PORTALS.map((p) => <PortalCard key={p.slug} p={p} total={totals[p.slug]} />)}
      </div>
    </section>
  );
};
